/* eslint-disable @typescript-eslint/no-explicit-any */
import { toErrors } from '@client/new/helpers/forms';
import { FieldPath, FieldValue, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { challengeSchema, ChallengeForm } from './ChallengeForm.schema';
import { validateChallengeForm } from './ChallengeForm.validation';
import { useEffect, useRef, useState } from 'react';
import { Actions, Async } from '@client/new/helpers/types';
import { getDispatchers } from '@client/new/helpers/types-utils';
import { LiteChallenge } from '../model/Challenges.types';
import { ChallengesVM } from './Challenges.viewModel';
import toast from 'react-hot-toast';
import { ChallengesModel } from '../model/Challenges.model';
import {
  completionRequirementsToggles,
  submissionRequirementsToggles,
} from '../model/Challenges.mapper';
import { getAllFieldPaths } from './utils';
import { trackSessionEvent } from '@services/session-tracker/sessionTracker';

export type ChallengeFormVM = ReturnType<typeof useChallengeFormVM>;

type ChallengeFormVMActionMap = {
  StartTemplateUpload: null;
  AddTemplate: string;
  RemoveTemplate: string;
  UploadTemplate: File;
};

type ChallengeFormVMAction = Actions<ChallengeFormVMActionMap>;

type UseChallengeFormVMParams = {
  challengeId?: string;
  data: ChallengeForm;
  onSubmit: (challenge: ChallengeForm) => Promise<LiteChallenge>;
  challengesVMDispatch: ChallengesVM['dispatch'];
  model: Async<ChallengesModel>;
  delta?: Partial<ChallengeForm>;
};

export const useChallengeFormVM = (p: UseChallengeFormVMParams) => {
  const [inProgress, setInProgress] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const { handleSubmit, formState, setValue, ...form } = useForm<ChallengeForm>(
    {
      values: p.data,
      reValidateMode: 'onChange',
      resolver: zodResolver(
        challengeSchema.superRefine(
          validateChallengeForm(
            p.model.type === 'fetched' ? p.model.value.challenges : [],
            p.challengeId,
          ),
        ),
      ),
    },
  );
  const setValueOf = <T extends FieldPath<ChallengeForm>>(target: {
    name: T;
    value: FieldValue<ChallengeForm>[T];
  }) => register(target.name).onChange({ target } as any);

  useEffect(() => {
    if (!p.delta) return;
    Object.entries(p.delta).map(([name, value]) =>
      setValueOf({ name: name as FieldPath<ChallengeForm>, value }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(p.delta)]);

  const values = form.watch();

  const register = <T = any>(key: FieldPath<ChallengeForm>) => {
    const { onChange: orgOnChange, ...orgRegister } = form.register(key);

    const error = form.getFieldState(key, formState)?.error?.message;
    const value = form.getValues(key);

    const onChange = (e: React.ChangeEvent<any>) => {
      setValue(key, e.target.value, { shouldValidate: true });

      if (key.includes('.date')) {
        const formData = form.getValues();
        const allFields = getAllFieldPaths(
          formData,
        ) as FieldPath<ChallengeForm>[];
        const dirtyFieldPaths = getAllFieldPaths(formState.dirtyFields);

        const nonDirtyDateFields = allFields.filter(
          (field) => field.includes('.date') && dirtyFieldPaths.includes(field),
        );

        if (nonDirtyDateFields.length > 0) form.trigger(nonDirtyDateFields);
      }

      if (key === 'showPhotosSection' && !e.target.value) {
        setValueOf({ name: 'submissionRequirements.photos', value: false });
        setValueOf({ name: 'completionRequirements.photos', value: false });
      }

      return orgOnChange(e);
    };

    return {
      ...orgRegister,
      error,
      value: value as T,
      onChange,
    };
  };

  const templates = register<string[]>('templates');
  const photoSection = register<boolean>('showPhotosSection');

  const submissionToggles = submissionRequirementsToggles(photoSection.value);
  const completionToggles = completionRequirementsToggles(photoSection.value);

  const onSubmit = (data: ChallengeForm) => {
    // if (model.type !== 'fetched') return;
    setInProgress(true);
    trackSessionEvent('ViewAction', {
      type: 'CreateChallenge',
      value: data,
    });

    const payload = data;
    p.onSubmit(payload)
      .then((d) => {
        p.challengesVMDispatch('CloseModal');
        form.reset();
        toast.success('Challenge created');
        trackSessionEvent('ViewAction', { type: 'ChallengeCreated' });
      })
      .catch((e) => {
        console.error('error creating', e);
        toast.error('Error creating challenge ' + (e?.message || ''));
        trackSessionEvent('ViewAction', {
          type: 'ChallengeCreatedError',
          value: 'Error creating challenge ' + (e?.message || ''),
        });
      })
      .finally(() => {
        setInProgress(false);
        setShowErrors(false);
      });
  };

  const _dispatch = (a: ChallengeFormVMAction) => {
    trackSessionEvent('ViewAction', a);
    switch (a.type) {
      case 'AddTemplate':
        setValue('templates', [...templates.value, a.payload], {
          shouldValidate: true,
        });
        break;

      case 'RemoveTemplate':
        setValue(
          'templates',
          templates.value.filter((i) => i !== a.payload),
        );
        break;

      case 'StartTemplateUpload':
        uploadInputRef.current?.click();
        break;

      case 'UploadTemplate': {
        if (p.model.type !== 'fetched') return;
        const currentUploads = templates.value.length;
        const remainingUploads = 5 - currentUploads;
        if (remainingUploads <= 0 || isUploading) return;
        setIsUploading(true);

        p.model.value
          .uploadFile(a.payload)
          .then((url) => dispatchers.dispatch('AddTemplate', url))
          .finally(() => setIsUploading(false));

        return;
      }
    }
  };

  const dispatchers = getDispatchers<ChallengeFormVMActionMap>(_dispatch);

  return {
    errors: showErrors ? toErrors(formState.errors) : null,
    inProgress,
    templates: templates.value,
    register,
    control: form.control,
    isUploading,
    values,

    submissionToggles,
    completionToggles,

    onSubmit: () => {
      if (inProgress) return;
      setShowErrors(true);
      handleSubmit(onSubmit)();
    },
    uploadInputRef,
    ...dispatchers,
  };
};
